import React, { useState, useEffect } from 'react';
import { getAuthToken } from "../../util/auth";
import { getApiChara } from "../../util/getApi";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  FaCalendarCheck,
  FaUserCheck,
  FaUserClock,
  FaUsers,
  FaPills,
  FaUserCog,
  FaHospitalUser,
  FaPlus,
  FaCalendarPlus,
  FaFileInvoiceDollar,
  FaProcedures,
  FaFileAlt,
  FaCogs
} from 'react-icons/fa';

const ECard = ({ hospitalData }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [appointmentValue, setAppointmentValue] = useState(0);
  const [userCount, setuserCount] = useState(0);
  const [patientCountValue, setPatientCountValue] = useState(0);
  const [medicineCountValue, setMedicineCountValue] = useState(0);
  const [todaysCheckedPatientCountValue, setTodaysCheckedPatientCountValue] = useState(0);
  const [unattendedPatientsCountValue, setUnattendPatientCountValue] = useState(0);
  const [ipdCount, setIpdCount] = useState(0);
  const [showIpdStats, setShowIpdStats] = useState(false);

  useEffect(() => {
    if (hospitalData && hospitalData.configurations) {
      const ipdConfig = hospitalData.configurations.find(
        (config) => config.paramName === "IPD Access" || config.paramCode === "boolIpdAccess"
      );
      if (ipdConfig && ipdConfig.paramValue === "true") {
        setShowIpdStats(true);
        fetchIpdCount();
      } else {
        setShowIpdStats(false);
      }
    }
  }, [hospitalData]);

  const fetchUpdatedAppointmentValue = async () => {
    try {
      const token = getAuthToken();
      if (token === null || token === 'EXPIRED') navigate('/');
      const apiChar = getApiChara();
      const response = await fetch(`${apiChar}/waitingList/getTodaysWaitingListCount`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      const data = await response.json();
      setAppointmentValue(typeof data === 'number' ? data : 0);
    } catch (error) {
      console.error('Error fetching appointment count:', error);
      setAppointmentValue(0);
    }
  };

  const fetchUpdatedpatientCountValue = async () => {
    try {
      const token = getAuthToken();
      if (token === null || token === 'EXPIRED') navigate('/');
      const apiChar = getApiChara();
      const response = await fetch(`${apiChar}/patient/getAllPatientsCount`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      const data = await response.json();
      setPatientCountValue(typeof data === 'number' ? data : 0);
    } catch (error) {
      console.error('Error fetching patient count:', error);
      setPatientCountValue(0);
    }
  };

  const fetchUpdatedMedicineCountValue = async () => {
    try {
      const token = getAuthToken();
      if (token === null || token === 'EXPIRED') navigate('/');
      const apiChar = getApiChara();
      const response = await fetch(`${apiChar}/medicine/getAllMedicineCount`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      const data = await response.json();
      setMedicineCountValue(typeof data === 'number' ? data : 0);
    } catch (error) {
      console.error('Error fetching medicine count:', error);
      setMedicineCountValue(0);
    }
  };

  const fetchUpdatedTodaysCheckedPatientCountValue = async () => {
    try {
      const token = getAuthToken();
      if (token === null || token === 'EXPIRED') navigate('/');
      const apiChar = getApiChara();
      const response = await fetch(`${apiChar}/waitingList/getTodaysWaitingListDoneCount`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      const data = await response.json();
      setTodaysCheckedPatientCountValue(typeof data === 'number' ? data : 0);
    } catch (error) {
      console.error('Error fetching checked patient count:', error);
      setTodaysCheckedPatientCountValue(0);
    }
  };

  const fetchUpdatedUnattentedPatientCountValue = async () => {
    try {
      const token = getAuthToken();
      if (token === null || token === 'EXPIRED') navigate('/');
      const apiChar = getApiChara();
      const response = await fetch(`${apiChar}/waitingList/getTodaysWaitingListNotDoneCount`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      const data = await response.json();
      setUnattendPatientCountValue(typeof data === 'number' ? data : 0);
    } catch (error) {
      console.error('Error fetching unattended patient count:', error);
      setUnattendPatientCountValue(0);
    }
  };

  const fetchUsersCountValue = async () => {
    try {
      const token = getAuthToken();
      if (token === null || token === 'EXPIRED') navigate('/');
      const apiChar = getApiChara();
      const response = await fetch(`${apiChar}/users/getAllUsersCount`, {
        headers: { Authorization: 'Bearer ' + token },
      });
      const data = await response.json();
      setuserCount(typeof data === 'number' ? data : 0);
    } catch (error) {
      console.error('Error fetching users count:', error);
      setuserCount(0);
    }
  };

  const fetchIpdCount = async () => {
    try {
      const token = getAuthToken();
      if (!token || token === "EXPIRED") return;
      const apiChar = getApiChara();
      const response = await fetch(`${apiChar}/ipd/admissions/active/count`, {
        headers: { Authorization: "Bearer " + token },
      });
      const data = await response.json();
      setIpdCount(typeof data === 'number' ? data : 0);
    } catch (error) {
      console.error("Error fetching IPD count:", error);
      setIpdCount(0);
    }
  };

  useEffect(() => {
    fetchUpdatedAppointmentValue();
    fetchUpdatedpatientCountValue();
    fetchUpdatedMedicineCountValue();
    fetchUpdatedTodaysCheckedPatientCountValue();
    fetchUpdatedUnattentedPatientCountValue();
    fetchUsersCountValue();
  }, []);

  const cardItems = [
    {
      title: t("Today's Appointments"),
      subtitle: t("Daily Overview"),
      value: appointmentValue,
      icon: <FaCalendarCheck />,
      bgClass: "bg-gradient-indigo",
    },
    {
      title: t("Checked Patients"),
      subtitle: t("Completed Today"),
      value: todaysCheckedPatientCountValue,
      icon: <FaUserCheck />,
      bgClass: "bg-gradient-teal",
    },
    {
      title: t("Remaining Patients"),
      subtitle: t("In Queue"),
      value: unattendedPatientsCountValue,
      icon: <FaUserClock />,
      bgClass: "bg-gradient-orange",
    },
    {
      title: t("Total Patients"),
      subtitle: t("All Time Records"),
      value: patientCountValue,
      icon: <FaUsers />,
      bgClass: "bg-gradient-slate",
    },
    {
      title: t("Total Medicines"),
      subtitle: t("Pharmacy Inventory"),
      value: medicineCountValue,
      icon: <FaPills />,
      bgClass: "bg-gradient-rose",
    },
    {
      title: t("Total Users"),
      subtitle: t("Active Staff"),
      value: userCount,
      icon: <FaUserCog />,
      bgClass: "bg-gradient-emerald",
    },
  ];

  if (showIpdStats) {
    cardItems.push({
      title: t("IPD Patients"),
      subtitle: t("Active IPD Cases"),
      value: ipdCount,
      icon: <FaHospitalUser />,
      bgClass: "bg-gradient-amber",
    });
  }

  const quickActions = [
    {
      title: t("Appointment"),
      icon: <FaCalendarPlus />,
      action: () => navigate("/dashboard/appointments"),
      color: "#4f46e5",
    },
    {
      title: t("Checkup List"),
      icon: <FaPlus />,
      action: () => navigate("/dashboard/checklist"),
      color: "#059669",
    },
    {
      title: t("Billing"),
      icon: <FaFileInvoiceDollar />,
      action: () => navigate("/dashboard/billing"),
      color: "#d97706",
    },
    {
      title: t("Reports"),
      icon: <FaFileAlt />,
      action: () => navigate("/dashboard/reports"),
      color: "#8b5cf6",
    },
  ];

  if (showIpdStats) {
    quickActions.push({
      title: t("IPD"),
      icon: <FaProcedures />,
      action: () => navigate("/dashboard/ipd"),
      color: "#db2777",
    });
  }

  return (
    <div className="container dashboard-container pt-0">
      <div className="mb-3">
        <div className="row g-3">
          {quickActions.map((action, index) => (
            <div key={index} className="col-6 col-sm-4 col-md-3 col-lg-2">
              <div
                className="card custom-action-card border-0 shadow"
                onClick={action.action}
              >
                <div className="card-body text-center d-flex flex-column align-items-center justify-content-center p-3">
                  <div
                    className="icon-circle mb-3"
                    style={{
                      backgroundColor: `${action.color}15`,
                      color: action.color,
                      width: '44px',
                      height: '44px',
                      fontSize: '1.2rem'
                    }}
                  >
                    {action.icon}
                  </div>
                  <h6 className="card-title mb-0 small fw-bold text-secondary" style={{ fontSize: '0.8rem' }}>{action.title}</h6>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-3">
        <div className="row g-4">
          {cardItems.map((item, index) => (
            <div key={index} className="col-6 col-sm-6 col-md-4 col-xl-3">
              <div className={`dash-card ${item.bgClass} shadow`}>
                <div className="dash-card-content">
                  <div className="dash-card-info">
                    <div className="dash-card-title">{item.title}</div>
                    <div className="dash-card-subtitle">{item.subtitle}</div>
                    <div className="dash-card-value">{item.value}</div>
                  </div>
                  <div className="dash-card-icon-wrapper">
                    {item.icon}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ECard;

